// ---------------------------------------------------------------------------
// Generic column-editor types and constants for the SpreadsheetEditor module.
//
// The QuoteTable feature originated these definitions; they are kept named
// `QuoteColumnType` for backward compatibility (re-exported from
// `QuoteTable/quote-table.types.ts`) but the semantics are generic to any
// spreadsheet-style column editor.
// ---------------------------------------------------------------------------

export type QuoteColumnType =
	| "description"
	| "response_quantity"
	| "response_value"
	| "saving"
	| "response_number"
	| "response_text"
	| "unit_of_measurement"
	| "total";

export interface ColumnConfig {
	id?: number;
	columnType: QuoteColumnType;
	label: string;
	localId: string;
	sortOrder: number;
}

export interface DefaultRowConfig {
	/**
	 * Per-column cell values keyed by `column.localId` (a stable UUID). Every
	 * description column (including the primary one) reads/writes its value
	 * here — there is no separate `name` slot on rows (cells-only model).
	 * Keyed by localId rather than sortOrder so values survive column add /
	 * remove / reorder operations (which renumber sortOrders).
	 */
	cells?: Record<string, string>;
	sort_order: number;
	unit_id: number | null;
}

export interface ColumnTypeOption {
	label: string;
	maxPerTable: number | null;
	value: QuoteColumnType;
}

export const MAX_COLUMNS = 20;
export const COLUMN_WARNING_THRESHOLD = 15;

export const COLUMN_TYPE_OPTIONS: ColumnTypeOption[] = [
	{ value: "description", label: "Description", maxPerTable: null },
	{ value: "unit_of_measurement", label: "Units", maxPerTable: 1 },
	{ value: "response_quantity", label: "Response: Quantity", maxPerTable: 1 },
	{ value: "response_value", label: "Response: Value", maxPerTable: null },
	{ value: "saving", label: "Response: Saving", maxPerTable: null },
	{ value: "response_number", label: "Response: Number", maxPerTable: null },
	{ value: "response_text", label: "Response: Text", maxPerTable: null },
	{ value: "total", label: "Total", maxPerTable: 1 },
];

export const COLUMN_TYPE_DESCRIPTIONS: Record<QuoteColumnType, string> = {
	description: "A text field for describing the scope item or line item name.",
	unit_of_measurement:
		"Dropdown for the unit of measure (e.g. m², hours, each). Only one per table.",
	response_quantity:
		"Numeric field where bidders enter the quantity. Only one per table.",
	response_value: "Numeric field where bidders enter a dollar value or rate.",
	saving:
		"Bidder enters a saving/discount amount (positive number); stored and shown as a negative value so it reduces the total.",
	response_number: "Generic numeric field for any number entry.",
	response_text: "Free-text field where bidders can type a response.",
	total:
		"Auto-calculated column that sums all Response: Value and Response: Saving columns. Requires at least one Value or Saving column. Only one per table.",
};

export const SAMPLE_CELL_LABELS: Record<QuoteColumnType, string> = {
	description: "Excavation works",
	unit_of_measurement: "m³",
	response_quantity: "120",
	response_value: "$85.00",
	saving: "-$500.00",
	response_number: "12",
	response_text: "On request",
	total: "$10,200.00",
};

export const BLANK_ROWS: DefaultRowConfig[] = [
	{ unit_id: null, sort_order: 1 },
	{ unit_id: null, sort_order: 2 },
	{ unit_id: null, sort_order: 3 },
	{ unit_id: null, sort_order: 4 },
];
